import { useCallback, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import {
  EarnedCardsFan,
  type FanAnchor,
} from '@/features/collection/components/earned-cards-fan'
import {
  clearEarnedCards,
  useHeldEarnedCards,
} from '@/features/collection/earned-cards-store'
import { COLLECTION_ANCHOR_ATTR } from '@/shared/lib/collection-tab'

/**
 * Where the Collection tab is, in viewport coordinates.
 *
 * Measured rather than derived from the tab count: the row is inset and its items are spaced, so
 * an even share of the width misses a tab's centre — the same reason Android reads its bar's own
 * geometry. Both navs are always mounted and hidden with `display:none` at their breakpoint, so
 * the one with a non-zero rect is the visible one, and no media query is restated in JS.
 */
function measureAnchor(): FanAnchor | null {
  const icons = document.querySelectorAll<HTMLElement>(
    `[${COLLECTION_ANCHOR_ATTR}]`,
  )
  for (const icon of icons) {
    const rect = icon.getBoundingClientRect()
    if (rect.width === 0) continue
    const bar = icon.closest('nav, header')
    if (!bar) continue
    const barRect = bar.getBoundingClientRect()
    // A bar past the middle of the viewport is below the fan; the desktop header is above it.
    const dir = barRect.top > window.innerHeight / 2 ? 1 : -1
    return {
      x: rect.left + rect.width / 2,
      y: dir === 1 ? barRect.top : barRect.bottom,
      dir,
    }
  }
  return null
}

/** A practice draws its own outro; the fan waits for the hub the user returns to. */
function isInsidePractice(pathname: string): boolean {
  return pathname.includes('/practice/')
}

/**
 * Draws the earned-cards fan once a completion has armed it and the user is somewhere it can play.
 *
 * Mounted by the app shell rather than the Collection tab: the cards are earned in a practice and
 * land on the nav, neither of which the Collection tab is on screen for.
 */
export function EarnedCardsOverlay() {
  const cards = useHeldEarnedCards()
  const { pathname } = useLocation()
  const [anchor, setAnchor] = useState<FanAnchor | null>(null)
  const playable = cards !== null && !isInsidePractice(pathname)

  useEffect(() => {
    if (!playable) {
      setAnchor(null)
      return
    }
    // After paint, so the nav the user just returned to has been laid out. Keyed on `playable`
    // alone: every tab route puts the nav in the same place, so only leaving the practice moves it.
    const id = requestAnimationFrame(() => setAnchor(measureAnchor()))
    return () => cancelAnimationFrame(id)
  }, [playable])

  // Held cards with nowhere to land — no Collection tab in this arm, or the nav is gone. Drop
  // them rather than waiting for a tab that is never coming.
  useEffect(() => {
    if (playable && anchor === null) {
      const id = setTimeout(clearEarnedCards, 1000)
      return () => clearTimeout(id)
    }
  }, [playable, anchor])

  const onFinished = useCallback(() => clearEarnedCards(), [])

  if (!playable || anchor === null || cards === null) return null
  return (
    <EarnedCardsFan cards={cards} anchor={anchor} onFinished={onFinished} />
  )
}
